/**
 * Laying overlapping blocks out side by side.
 *
 * Two meetings at ten o'clock are two things you have to see, and stacking them means
 * seeing one. This is the column assignment every calendar does: find the runs of
 * mutually overlapping blocks, and within each run give every block the narrowest column
 * that is free when it starts.
 *
 * Pure and separate from the component so the awkward cases — a block that overlaps two
 * others which do not overlap each other, a zero-length reminder, a chain that widens
 * and narrows again — are testable without a browser.
 */

export interface Span {
  id: string;
  startMinutes: number;
  endMinutes: number;
}

export interface Placement {
  /** Which column this block sits in, from zero. */
  column: number;
  /** How many columns its cluster needs, so the width is `1 / columns`. */
  columns: number;
}

/**
 * A zero-length block still needs to be seen, and would otherwise overlap nothing and
 * be overlapped by nothing. Reminders are exactly this (§5.6).
 */
const MIN_SPAN_MINUTES = 1;

const endOf = (span: Span): number =>
  Math.max(span.endMinutes, span.startMinutes + MIN_SPAN_MINUTES);

/**
 * Assign each span a column and its cluster's width.
 *
 * A "cluster" is a maximal run of spans connected by overlap — not merely spans that
 * overlap each other. If A overlaps B and B overlaps C, all three share a width even
 * where A and C do not touch, because otherwise B would have nowhere to sit.
 */
export function layoutOverlaps(spans: readonly Span[]): Map<string, Placement> {
  const placements = new Map<string, Placement>();
  if (spans.length === 0) return placements;

  // Earliest first; longer first on a tie, so the long block takes the left column and
  // the short ones stack beside it rather than pushing it about.
  const ordered = [...spans].sort((a, b) => a.startMinutes - b.startMinutes || endOf(b) - endOf(a));

  let cluster: { span: Span; column: number }[] = [];
  /** When each column becomes free. Index is the column number. */
  let columnEnds: number[] = [];

  const flush = () => {
    const columns = Math.max(1, columnEnds.length);
    for (const member of cluster) {
      placements.set(member.span.id, { column: member.column, columns });
    }
    cluster = [];
    columnEnds = [];
  };

  for (const span of ordered) {
    // A gap where every column is free ends the cluster: nothing after this point can
    // overlap anything before it, so the next run starts at full width again.
    if (columnEnds.length > 0 && columnEnds.every((end) => end <= span.startMinutes)) {
      flush();
    }

    // First column free at this moment, or a new one.
    let column = columnEnds.findIndex((end) => end <= span.startMinutes);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(endOf(span));
    } else {
      columnEnds[column] = endOf(span);
    }

    cluster.push({ span, column });
  }
  flush();

  return placements;
}
